import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import cnh from '~/helpers/cnh';
import tooltipStyles from '~/styles/tooltip.module.css';

type Props = {
  content: string;
  children: React.ReactNode;
  triggerClassName?: string;
};

export default function ClickTooltip({
  content,
  children,
  triggerClassName,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipId = useId();

  const close = useCallback(() => {
    setIsOpen(false);
    buttonRef.current?.focus();
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((open) => !open);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };

    const handlePointerDown = (event: MouseEvent) => {
      if (wrapperRef.current?.contains(event.target as Node)) {
        return;
      }

      close();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isOpen, close]);

  return (
    <span className={tooltipStyles.wrapper} ref={wrapperRef}>
      <button
        ref={buttonRef}
        type="button"
        className={cnh(tooltipStyles.trigger, triggerClassName ?? '')}
        aria-expanded={isOpen}
        aria-controls={tooltipId}
        aria-describedby={isOpen ? tooltipId : undefined}
        onClick={toggle}
      >
        {children}
      </button>

      {isOpen && (
        <div
          id={tooltipId}
          role="tooltip"
          className={tooltipStyles.tooltip}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: content }}
        />
      )}
    </span>
  );
}
